'use strict';

import TS from "@zerolight/zlthunder-time-utils";
import config from "../../../shared/config";
import CentralConfigClient from "../../../shared/central-config-client";
import {apiCall} from "../../../shared/api-client";
import {getIn} from "../../../shared/utils";

import {extendFilter, addSummaries} from "../shared";
import {Day14Intervals, Day7Intervals, LifeIntervals} from "../../../shared/intervals";

import {Map as ImmutableMap} from "immutable";
import {useMemo, useState} from "react";
import {useStaleDataLoader} from "../state";

const GeneratedImagesType = ['2DODFrame', 'Complete'];
const CachedImagesType = null;

function aggregate2dUsage(summaries, intervals, add = 0) {
	if (!summaries) {
		return null;
	}
	intervals = new Set(intervals);
	let usage = {count: 0, stale: false};
	for (let s of summaries) {
		let si = getIn(s, ['interval', 'name'], null);
		if (intervals.has(si)) {
			usage = addSummaries(usage, s);
		}
	}
	if (usage.stale) {
		return null;
	}
	return usage.count + add;
}

const unset = Symbol('unset');

class CustomerInfoStateLoader {
	constructor() {
		this.billingConfig = null;
		this.usage3d = null;
		this.usage2d = null;
	}

	loadBillingConfig() {
		if (this._billingConfigPromise) {
			return this._billingConfigPromise;
		}

		this._billingConfigPromise = (async () => {
			let ccConfig = config.get('BillingConfiguration');
			if (!ccConfig) {
				throw new Error(`Central configuration client isn't configured`);
			}
			let ccClient = new CentralConfigClient(ccConfig);
			let {fallbackConfiguration: fallback, subProject = null} = ccConfig;
			let response, project;
			try {
				response = await ccClient.get();
				({project} = response);
			} catch (err) {
				console.error('Unable to fetch billing configuration', err);
				return Object.assign({}, fallback);
			}
			let {billingConfiguration, subProjects} = project;
			this.billingConfig = Object.assign({subProjects, subProject}, fallback, billingConfiguration);
			return this.billingConfig;
		})()
			.catch((err) => {
				console.error('Failed to load the billing configuration', err);
				this._billingConfigPromise = null;
			});

		return this._billingConfigPromise;
	}

	async loadUsage3d(filter) {
		let response = await apiCall({method: 'dashboard/customer-info', params: filter});
		this.usage3d = response.data;
		return this.usage3d;
	}

	async loadUsage2d() {
		let params = {type: GeneratedImagesType};
		let filter = config.get('CustomerInfo.Usage2DFilter', null);
		// let filter = config.get('CustomerInfo', null);
		if (filter) {
			params = Object.assign(params, filter);
		}

		let response = await apiCall({
			method: '/stats/dashboard/data',
			params, applyConfiguredFilters: false,
			//Custon merge in case of data for sum servers
			mergeCustomiser: function (a, b, key) {
				if (key === 'data') {
					if (b) {
						const getStale = (a => Boolean(a && a.stale));
						a.stale = getStale(a) || getStale(b);
						for (let i = 0; i < a['summaries'].length; i++) {
							a['summaries'][i]['count'] += b['summaries'][i]['count'];
							a['summaries'][i]['total'] += b['summaries'][i]['total'];
						}
					} 
					return a;
				}
				// return undefined
			}
		});
		this.usage2d = response.data;
		return this.usage2d;
	}

	async _load() {
		await this.loadBillingConfig();
		if (this.show3d && this.stale3d) {
			let currentSubProject = this.currentSubProject;
			if (currentSubProject) {
				let filter = this.getSubProjectsFilter([currentSubProject]);
				await this.loadUsage3d(filter);
			} else {
				let trials = this.getTrialSubProjects();
				let filter = this.getSubProjectsFilter(trials);
				await this.loadUsage3d(filter);
			}
		}
		if (this.show2d && this.stale2d) {
			await this.loadUsage2d();
		}
	}

	load() {
		if (!this._loading) {
			this._loading = Promise.resolve(this._load())
				.then(
					() => {
						this._loading = null;
						return this;
					},
					(err) => {
						console.error(err);
						this._loading = null;
						return this;
					}
				);
		}
		return this._loading;
	}

	reload() {
		this._loading = null;
		this.usage2d = null;
		this.usage3d = null;
		return this.load();
	}

	get currentSubProject() {
		return getIn(this.billingConfig, 'subProject', null);
	}

	get show3d() {
		return true;
	}

	get stale3d() {
		let {usage3d} = this;
		return !usage3d || (usage3d.stale === false);
	}

	get show2dGenerated() {
		let show = getIn(this.billingConfig, 'showGeneratedImages', unset);
		if (show === unset) {
			show = Boolean(config.get('show2DGeneratedUsage'));
			return show;
		} else {
			return Boolean(show);
		}
	}

	get show2dCached() {
		let show = getIn(this.billingConfig, 'showCachedImages', unset);
		if (show === unset) {
			return Boolean(config.get('show2DCachedUsage'));
		} else {
			return Boolean(show);
		}
	}

	get show2d() {
		return this.show2dGenerated || this.show2dCached;
	}

	get stale2d() {
		let {usage2d} = this;
		return !usage2d || (usage2d.stale === false);
	}

	clear() {
		this.billingConfig = null;
		this.usage3d = null;
		this.usage2d = null;
	}

	getSubProjects() {
		let sbps = getIn(this.billingConfig, 'subProjects');
		if (sbps) {
			return Object.keys(sbps);
		} else {
			return null;
		}
	}

	getTrialSubProjects() {
		return getIn(this.billingConfig, ['trials', 'ongoing'], null);
	}

	getSubProjectsFilter(subProjects = null) {
		if (subProjects === null) {
			return {};
		}

		let filter = {};
		let {billingConfig} = this;

		for (let sbp of subProjects) {
			let sbpFilter = getIn(billingConfig, ['subProjects', sbp, 'conditions']);
			if (sbpFilter) {
				filter = extendFilter(filter, sbpFilter);
			} else {
				console.warn(
					`Market with ongoing trial doesn't have the condition defined, ` +
					`market's usage won't show`, sbp);
			}
		}
		return filter;
	}

	getTrialAllocation(subProjects = null) {
		let {billingConfig} = this;
		if (subProjects === null) {
			try {
				subProjects = Object.keys(billingConfig.subProjects);
			} catch (err) {
				return 0;
			}
		}
		let allocation = 0;
		for (let sbp of subProjects) {
			allocation +=
				getIn(
					billingConfig,
					['subProjects', sbp, 'billingConfiguration', 'trialAllocation'], 0);
		}
		return allocation;
	}

	getLimit3d() {
		let limit = getIn(this.billingConfig, 'limit3DMinutes');
		return (limit && limit !== -1) ? Number(limit) : null;
	}

	getLimit2dGenerated() {
		let limit = getIn(this.billingConfig, 'limit2DGeneratedImages', null);
		return (limit && limit !== -1) ? Number(limit) : null;
	}

	getUsage3dLife() {
		let usage = getIn(this.usage3d, 'life', 0);
		let trials = this.getTrialSubProjects();
		if (trials !== null) {
			// Include the allocated minutes for projects with expired trials...
			trials = new Set(trials);
			let projects = this.getSubProjects();
			projects = projects.reduce((a, sbp) => {
				if (!trials.has(sbp)) {
					a.push(sbp);
				}
				return a;
			}, []);
			let allocation = this.getTrialAllocation(projects);
			usage += allocation * TS.MINUTE;
		}
		return usage;
	}

	getUsage3dDay7() {
		let usage = getIn(this.usage3d, 'day7', 0);
		return usage;
	}

	getUsage3dDay14() {
		let usage = getIn(this.usage3d, 'day14', 0);
		return usage;
	}

	getAllowance3d() {
		let trials = this.getTrialSubProjects();
		let usage = getIn(this.usage3d, 'life', 0);
		if (trials) {
			let allocated = this.getTrialAllocation(trials);
			return allocated * TS.MINUTE - usage;
		} else {
			let limit = this.getLimit3d();
			return limit * TS.MINUTE - usage;
		}
	}

	getAllowance2d() {
		let limit = this.getLimit2dGenerated();
		let usage = this.getUsage2dGeneratedLife();
		return limit - usage;
	}

	getUsage2dGeneratedLife() {
		return aggregate2dUsage(
			getIn(this.usage2d, 'summaries'),
			LifeIntervals,
			getIn(this.billingConfig, 'additionalImageCount', 0)
		);
	}

	getUsage2dGeneratedDay7() {
		return aggregate2dUsage(
			getIn(this.usage2d, 'summaries'),
			Day7Intervals);
	}

	getUsage2dGeneratedDay14() {
		return aggregate2dUsage(
			getIn(this.usage2d, 'summaries'),
			Day14Intervals);
	}

	getUsage2dCached() {
		if (!this.usage2d || !CachedImagesType) {
			return 0;
		}
		return this.usage2d.get(CachedImagesType, 'count', 0);
	}

	getSubProjectLimit3d(subProject = null) {
		subProject = subProject || this.currentSubProject;
		if (subProject) {
			return getIn(
				this.billingConfig,
				['subProjects', subProject, 'billingConfiguration', 'limit3DMinutes'], null);
		}
		return null;
	}

	getSubProjectAllowance3d(subProject = null) {
		subProject = subProject || this.currentSubProject;
		if (subProject) {
			let limit = this.getSubProjectLimit3d(subProject);
			let usage = getIn(this.usage3d, 'life', 0);
			return limit * TS.MINUTE - usage;
		} else {
			return null;
		}
	}
}


const loader = new CustomerInfoStateLoader();
const InitialState = new ImmutableMap({
	stale: true, usage3dLife: null, usage3dDay7: null, usage3dDay14: null,
	usage2dGeneratedLife: null, usage2dGeneratedDay7: null, usage2dGeneratedDay14: null,
	usage2dCached: null,
	limit3d: null, allowance3d: null,
	limit2dGenerated: null, limit2dCached: null, show3dUsage: true,
	show2dGeneratedUsage: false,
	show2dCachedUsage: false,
	alertThreshold: config.get('alertThreshold') || 7200,
	subProject: null, subProjectLimit3d: null, subProjectAllowance3d: null
});

function useCustomerInfoState() {
	const [state, setState] = useState(() => {
		return new ImmutableMap(InitialState.toMap());
	});

	const updateState = useMemo(() => {
		return cb => setState(state => state.withMutations(cb));
	}, [setState]);

	useStaleDataLoader(async (cancelled) => {
		await loader.load();
		if (loader.stale3d) {
			// Tell the loader to reload
			return true;
		}
		if (!cancelled.current) {
			// TODO: refactor this on occasion
			updateState(state => {
				state.set('stale', false);
				state.set('usage3dLife', loader.getUsage3dLife());
				state.set('usage3dDay7', loader.getUsage3dDay7());
				state.set('usage3dDay14', loader.getUsage3dDay14());
				state.set('usage2dGeneratedLife', loader.getUsage2dGeneratedLife());
				state.set('usage2dGeneratedDay7', loader.getUsage2dGeneratedDay7());
				state.set('usage2dGeneratedDay14', loader.getUsage2dGeneratedDay14());
				state.set('usage2dCached', loader.getUsage2dCached());
				state.set('show2dGeneratedUsage', loader.show2dGenerated);
				state.set('show2dCachedUsage', loader.show2dCached);
				state.set('trialAllocation', loader.getTrialAllocation());
				state.set('limit3d', loader.getLimit3d());
				state.set('allowance3d', loader.getAllowance3d());
				state.set('allowance2dGenerated', loader.getAllowance2d());
				let sbp = loader.currentSubProject;
				if (sbp) {
					state.set('subProject', sbp);
					state.set('subProjectLimit3d', loader.getSubProjectLimit3d(sbp));
					state.set('subProjectAllowance3d', loader.getSubProjectAllowance3d(sbp));
				} else {
					state.set('subProject', null);
					state.set('subProjectLimit3d', loader.getSubProjectLimit3d(null));
					state.set('subProjectAllowance3d', loader.getSubProjectAllowance3d(null));
				}
			});
		}
	}, [updateState]);
	return state.toJS();
}


export default CustomerInfoStateLoader;
export {
	useCustomerInfoState, InitialState,
	GeneratedImagesType, CachedImagesType
};